import { useQuery } from '@tanstack/react-query';
import type { ProviderStatus } from '@contracts/market';
import { api } from '../lib/api';
import { clock, humanize, ms } from '../lib/format';
import { useLive } from '../store/live';
import { Panel } from './Panel';

export function ProviderPanel() {
  const live = useLive((s) => s.provider);
  const query = useQuery({
    queryKey: ['provider'],
    queryFn: () => api.get<ProviderStatus>('/api/v1/market/provider'),
    refetchInterval: 5000,
  });
  const p = live ?? query.data;
  return (
    <Panel title="Market-data provider" meta={p ? `${p.mode} mode` : undefined}>
      {!p ? (
        <p className="muted small">{query.isError ? 'Provider status unavailable.' : 'Loading provider status…'}</p>
      ) : (
        <dl className="kv">
          <dt>Provider</dt>
          <dd>
            {p.provider}
            {p.isFallback ? ' (SIMULATED FALLBACK)' : ''}
          </dd>
          <dt>Feed state</dt>
          <dd>{p.label}</dd>
          <dt>Data quality</dt>
          <dd>{humanize(p.dataQuality)}</dd>
          <dt>Symbol</dt>
          <dd>
            {p.symbol} → {p.providerSymbol}
          </dd>
          <dt>Market</dt>
          <dd>{p.marketOpen ? 'Open' : 'Closed'}</dd>
          <dt>Quote age</dt>
          <dd className="num">{ms(p.quoteAgeMs)}</dd>
          <dt>Feed latency (last / p50)</dt>
          <dd className="num">
            {ms(p.feedLatencyMs)} / {ms(p.feedLatencyP50Ms)}
          </dd>
          <dt>Tick rate</dt>
          <dd className="num">{p.tickRatePerSecond.toFixed(1)}/s</dd>
          <dt>Last heartbeat</dt>
          <dd>{clock(p.lastHeartbeatAt)}</dd>
          <dt>Reconnects</dt>
          <dd className="num">{p.reconnectCount}</dd>
          <dt>Stale episodes</dt>
          <dd className="num">{p.staleCount}</dd>
          <dt>Circuit breaker</dt>
          <dd>{humanize(p.circuitState)}</dd>
          <dt>History loaded</dt>
          <dd>{p.historyLoaded ? 'Yes' : 'No'}</dd>
          <dt>Signals allowed</dt>
          <dd>{p.dataUsable ? 'Yes' : `No — ${humanize(p.blockReason)}`}</dd>
          {p.errorCode ? (
            <>
              <dt>Last error</dt>
              <dd>
                {humanize(p.errorCode)}
                {p.errorMessage ? <div className="small muted">{p.errorMessage}</div> : null}
              </dd>
            </>
          ) : null}
          <dt>Price precision / tick</dt>
          <dd className="num">
            {p.instrument.pricePrecision} dp / {p.instrument.tickSize}
          </dd>
          <dt>Contract size</dt>
          <dd className="num">
            {p.instrument.contractSize} oz/lot · min {p.instrument.minLot}
          </dd>
        </dl>
      )}
    </Panel>
  );
}
